// R5.7b — `toLegacyQualityRead`: THE NEW ENGINE'S READ, IN THE LEGACY READ'S DATA MODEL.
//
// OWNER DIRECTIVE (2026-08-22, final): "we iterated a lot to arrive at the current UI; copy it AS IS — the change
// is the content from the engine, not the display. I don't intend to rebuild or redesign." So the review surface
// renders the legacy `QualityReadView` presentation — its cards, sections, "Review this section" way-in, entry
// rows, recommendations, specialty panel, exactly as iterated — and THIS module is the only thing that changed:
// it supplies that presentation's data model (`QualityRead`) from `RubricReadPublicV1` plus the fact ledger.
//
// THREE RULES, all load-bearing:
//
//   1. EVERY SYSTEM STRING THIS ADAPTER SUPPLIES IS REGISTERED COPY (`rubricCopy.ts`), resolved through `COPY[id]`
//      — never a legacy scoring word, never a rating, never free text. Where a legacy slot wants a rating enum
//      (`rating`), the value is never rendered (the slots that would render it are left empty: `total: 0` on a
//      CV-section card, no `evidence`, no `assessment`), and the headline slot the legacy view renders in NEUTRAL
//      ink (`smallSectionState`) carries the registered band word instead. Applicant text — entry titles, the
//      organization, the entry's own text — comes from the ledger through the existing heading builder
//      (`entryHeadingOf`) and is the applicant's verbatim words, exactly as the legacy view already showed them.
//
//   2. THE BANNED-OUTPUT SCAN RUNS HERE TOO. A registered id that somehow failed the scan is dropped (rendered as
//      the empty string), never shown — the same fail-closed rule the rubric chokepoint (`Copy`) applies. Nothing
//      in this module can widen what the registry permits.
//
//   3. NOTHING IS ORDERED BY BAND OR CONTENT (T-ORDER-1). Domains keep `DOMAIN_ORDER` (in the opening line); CV
//      sections keep the document's first-appearance order; entries keep the read's document order; rows keep the
//      engine's flag order then recommendation order. The legacy "Start here" badge can never land: every card
//      sits at the legacy's calm urgency rank (no 'verify' cautions, no density rating), so `startHereIndex`
//      returns -1.
//
// WHAT MAPS WHERE (the table the build report carries) — OWNER DIRECTIVE 2026-08-23 ("this is not like the original…
// Go and copy it and maintain as is; you can adjust the added contents but the FLOW should remain the same"): the
// legacy FLOW is L1 = the CV's OWN SECTIONS as cards → L2 = one section's entries (left) beside their feedback
// (right). Nothing else is a card.
//
//   review confidence      → the legacy opening line (`lead`, and the first sentence of `overall`): the CV-level
//                            `CS-REVIEW-CONF-*` for the engine's level (A.5-P.15)
//   domain bands           → the opening line (`overall`), after the confidence: one registered `CS-OVERVIEW-BAND-SENTENCE`
//                            per domain in `DOMAIN_ORDER` — "{CS-DOMAIN-*}: {band}." where the band is the
//                            `CS-BAND-*` label, or for a "Not shown" domain with a valid closed-world scope the §4.7
//                            claim IN FULL (`CS-NOT-SHOWN`, `CS-NOT-SHOWN-EXTENDED`, `CS-OMISSION-SCOPE` with the
//                            scope's own count — C2b; without a valid scope the band label alone) — followed by the
//                            domain's §4.6 line when it has one (`CS-WHAT-WOULD-CHANGE` with the engine's target
//                            entry, else `CS-SATURATION`). NO domain cards.
//   presentation band      → the opening line, after the domains: `CS-OVERVIEW-BAND-SENTENCE` for the aggregate
//                            (`CS-SECTION-PRESENTATION`: `CS-PBAND-*`, custom CV only, §4.10) then one per visible
//                            sub-element (`CS-PSUB-*`: `CS-PBAND-*`). NO presentation cards. (The legacy
//                            `canmedsCap` slot renders only inside the legacy L3 "What this review looks for"
//                            details, which this route nulls — its method prose describes the legacy engine — so
//                            the presentation band is carried in the opening line instead.)
//   entries                → the CV's own sections (the ledger's `source_section`, registered `CS-VAL-SOURCE-
//                            SECTION-*` label; a section outside the closed enum by the CV's own taxonomy display
//                            name via its grounded `section_heading`; document order), ONE LEGACY SECTION CARD PER
//                            SECTION with the legacy card chrome ("▸ details", "Do this first", "Review this
//                            section · N items"); each entry = one legacy item with the applicant's title /
//                            organization from the ledger, its own text as the item's point (minus a leading line
//                            that is exactly the section slug — `displayedEntryText`)
//   section "Do this first"→ the section's FIRST prompt (the first row of its first entry with rows: a flag's
//                            `CS-FLAG-*` or a question's `CS-Q-*`, each with its `CS-OMISSION-SCOPE-ENTRY`
//                            disclosure — the same registered text the row carries at L2), else `CS-NOTHING-FLAGGED`.
//                            The legacy card appends its own pointer to the section's first flagged entry (legacy
//                            chrome + the applicant's title). The headline slot (`smallSectionState`) stays EMPTY
//                            (`total: 0`): no rating, no band, no count on a section card.
//   flags (Layer 7)        → the item's rows (the legacy "Clarify" rows, `CS-LEGACY-ROW-ACTION`): diagnosis = the
//                            flag's `CS-FLAG-*`, question = the per-entry scope disclosure `CS-OMISSION-SCOPE-ENTRY`.
//                            C2b FAIL-CLOSED: a flag whose scope is missing or invalid is NOT a row (the prompt is
//                            not made without the closed world it was assessed over)
//   questions (Layer 9)    → the item's rows: diagnosis = `CS-REC-KIND-QUESTION`, question = `CS-Q-*` followed by
//                            the recommendation's own scope disclosure `CS-OMISSION-SCOPE-ENTRY` (C2b). A question
//                            whose closed-world scope is missing or invalid is NOT a row
//   entries with no rows   → the section's "Nothing flagged in these entries" block (`strongEntries`), title + text
//   ledger-only entries    → items with no rows (present so the correction loop can reach them)
//   specialty overlay      → NOT adapted: the rubric specialty panel renders in the legacy panel's position (the
//                            legacy chip panel cannot carry the hedge / pinned sources / scopes / evidence)
//   assessed_at / stale    → the legacy "Reviewed {when}" caption and the re-run control (wired by the view)
//
// WHAT DOES NOT MAP (no legacy slot carries it without legacy copy that would misdescribe it) and is NOT rendered on
// the legacy read card (R5.7b fix, Claude+Sol adjudicated under owner delegation 2026-08-22: no new block inside a
// legacy card): the three facet levels (Layer 2; every legacy per-row slot carries an instruction or accuracy-check
// framing that would contradict §3.0 "no level is better than another"), the descriptors (Layer 4), the per-entry
// confidence line (Layer 8), and the known-fact / suggested-phrasing recommendations. They are reported on the
// facts screen ("Facts read from your CV", the rubric ledger panel below the legacy body), which every legacy
// entry card reaches through ONE text link (`CS-ENTRY-FACTS-LINK`) in its existing action area — a link, not a
// block. The domain evidence reveals, qualifying-entry links and CanMEDS convention note have no legacy slot and are
// not rendered on this surface.
import type { RubricReadPublicV1 } from '../../../workers/src/lib/rubricReadPublic';
import { enumLabelFor } from '../../../workers/src/lib/factLabels';
import {
  CLARIFICATION_GATE_PREFIX,
  type QItem, type QSiblingBullet, type QStrongEntry, type QWeakBullet, type QualityRead, type SectionQuality,
} from '../QualityReadView';
import { COPY, type CopyStringId } from './copy';
import { fillTemplate } from './renderContent';
import { scanSystemText } from './bannedOutput';
// C2b — a claim is a row here ONLY when its closed-world scope passes the strict checker (fail-closed).
import { scopeValid } from './scope';
import { entryHeadingOf, type EntryHeading, type LedgerPayload } from './evidenceIndex';
import { terminalFact } from '../../../workers/src/lib/terminalFacts';
// The CV's own section taxonomy — the conventional section each authoring-taxonomy id renders under, which is the
// label the legacy read grouped entries by. A pure-constant module (no runtime imports), like `factLabels`.
import { OUTPUT_SECTIONS } from '../../../workers/src/lib/renderModel';
import { showsAggregateBand, visibleSubElements } from './PresentationPanel';

type Entry = RubricReadPublicV1['entries'][number];
type Rec = RubricReadPublicV1['recommendations'][number];

/** A ledger entry, as far as this adapter reads it. Narrowed field by field; nothing is trusted by shape. */
interface LedgerEntryLike {
  entry_id?: unknown;
  raw_text?: unknown;
  source_section?: unknown;
  facts?: unknown;
  role_title?: unknown;
  institution?: unknown;
  date_start?: unknown;
  date_end?: unknown;
}

const NOT_STATED = 'not_stated';

function stated(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 && v !== NOT_STATED ? v : null;
}

/**
 * A registered string, scanned. The ONLY way a system string enters the adapted payload.
 *
 * Fail-closed like the rubric chokepoint: a registry member that fails the banned-output scan yields the empty
 * string, which every legacy slot renders as nothing. (`CS-INTENDED-USE*` are allowlisted by id; nothing here
 * resolves them.)
 */
export function copyText(id: CopyStringId): string {
  const text: string = COPY[id];
  return scanSystemText(text, id).ok ? text : '';
}

/**
 * The legacy item rows' non-rendered gate tag. Attribute-only in the legacy view; never shown. Both carry the
 * `CLARIFICATION_GATE_PREFIX` the legacy point list keys its NEUTRAL ink on (a clarification prompt is never
 * warning-red, §8.3 F2).
 */
export const ROW_GATE_FLAG = `${CLARIFICATION_GATE_PREFIX}flag`;
export const ROW_GATE_QUESTION = `${CLARIFICATION_GATE_PREFIX}question`;
/** The legacy item's own non-rendered gate tag. */
const ITEM_GATE = 'rubric';

/**
 * The id of one of the entry's LINES in the legacy left column. A SYSTEM id, never shown.
 *
 * It carries the line's INDEX as of 2026-08-23 (Sol review). Before that the whole of a multi-line entry was one
 * "point" with the id `${entry_id}::text`, so an entry with six bullets rendered as ONE row — which is why the
 * surface still did not behave like the legacy read no matter what the block around it looked like. The index is
 * derived from the entry's own displayed text and is stable for a given text; it is NOT a stable source-line id
 * from the ledger, which is what a per-line FINDING would need (see `rowsForEntry`).
 */
export function entryTextPointId(entry_id: string, index = 0): string {
  return `${entry_id}::text::${index}`;
}
function flagRowId(entry_id: string, i: number): string {
  return `${entry_id}::flag::${i}`;
}
function questionRowId(entry_id: string, i: number): string {
  return `${entry_id}::q::${i}`;
}

interface LedgerView {
  byId: ReadonlyMap<string, LedgerEntryLike>;
  headings: ReadonlyMap<string, EntryHeading>;
  /** Ledger order — the order ledger-only entries are appended in. */
  order: readonly string[];
}

function viewLedger(ledger: LedgerPayload | null | undefined): LedgerView {
  const byId = new Map<string, LedgerEntryLike>();
  const headings = new Map<string, EntryHeading>();
  const order: string[] = [];
  for (const e of ledger?.entries ?? []) {
    const rec = (e ?? {}) as LedgerEntryLike;
    const id = stated(rec.entry_id);
    if (id === null || byId.has(id)) continue;
    byId.set(id, rec);
    order.push(id);
    const heading = entryHeadingOf(rec);
    if (heading !== null) headings.set(id, heading);
  }
  return { byId, headings, order };
}

/* ============================================================================================================== *
 * THE ENTRY → ITEM MAPPING
 * ============================================================================================================== */

/**
 * The legacy item's title / organization for an entry — THE SAME RULE `EntryLabel` used (role title → institution
 * → raw text). The organization rides in the legacy `entry_organization` slot only when the title rung is the
 * role title and an institution is stated, which is what the legacy "Title — Org" heading was for.
 */
function identityOf(id: string, ledger: LedgerView): { entry_title: string; entry_organization?: string } {
  const heading = ledger.headings.get(id);
  if (!heading) return { entry_title: '' };
  const rec = ledger.byId.get(id);
  // SLUG-LEAK FIX: a title NEVER begins with its own section's slug or display heading. On the `raw_text` rung the
  // whole chunk is the title, so it goes through the same `displayedEntryText` the text point uses; on the stated
  // rungs the prefix strip runs defensively (an importer that folded the label into `role_title` cannot leak it
  // either). Only the label is removed — the applicant's own words are returned verbatim.
  const aliases = sectionAliasesOf(rec);
  const title = (heading.source === 'raw_text'
    ? (displayedEntryText(rec) ?? '')
    : stripSectionPrefix(heading.text, aliases)).replace(/^[\s]+/, '');
  if (title.length === 0) return { entry_title: '' };
  const institution = stated(rec?.institution);
  return heading.source === 'role_title' && institution !== null && institution !== title
    ? { entry_title: title, entry_organization: institution }
    : { entry_title: title };
}

/* -------------------------------------------------------------------------------------------------------------
 * THE SECTION-SLUG LEAK (owner, live on d26f3e98: "Slug leakage everywhere")
 *
 * The ledger assembles the document-supplied section label as the entry's FIRST LINE, so `${id}:section_heading`
 * has a real span to ground on. Two things then leak it into applicant-facing text:
 *   • the entry TITLE — `entryHeadingOf`'s last rung is `raw_text` VERBATIM, so an entry with no stated
 *     `role_title`/`institution` was titled with the whole chunk, label line included; HTML collapses the newline
 *     and it renders as "professional-memberships Alberta International Medical Graduates Association (AIMGA)".
 *   • the entry TEXT — the 2026-08-23 strip only removed a first line matching `source_section` or the grounded
 *     `section_heading` BYTE-FOR-BYTE, so it missed every case where the two differ in case, spacing or spelling
 *     (`professional-memberships` is outside the closed §2.2 enum, so `source_section` degrades to `unlabelled`
 *     and never matches), and every ledger predating the `section_heading` fact.
 *
 * `sectionAliasesOf` is the closed set of strings that count as THIS entry's section label, and
 * `stripSectionPrefix` / `isSectionLine` are the only two places the strip happens.
 * ----------------------------------------------------------------------------------------------------------- */

/** Every string that names THIS entry's section: its `source_section`, its grounded heading, and the display names. */
export function sectionAliasesOf(rec: LedgerEntryLike | undefined): string[] {
  const raw = [stated(rec?.source_section), sectionHeadingOf(rec)].filter((x): x is string => x !== null);
  const out: string[] = [...raw];
  for (const r of raw) {
    const taxonomy = taxonomySectionLabel(r);
    if (taxonomy !== null) out.push(taxonomy);
    const enumId = enumLabelFor('source_section', normaliseSourceSection(r));
    // `unlabelled` / `other` are the enum's own catch-alls ("No section heading", "Other") — never a heading a
    // document printed, and both are ordinary English words. They are NOT strippable.
    if (enumId !== null && enumId !== 'CS-VAL-SOURCE-SECTION-UNLABELLED' && enumId !== 'CS-VAL-SOURCE-SECTION-OTHER') {
      const label = COPY[enumId];
      if (typeof label === 'string' && label.length > 0) out.push(label);
    }
  }
  const seen = new Set<string>();
  return out.filter((a) => {
    const k = normaliseSectionLabel(a);
    if (k.length === 0 || seen.has(k)) return false;
    seen.add(k);
    return true;
  }).sort((a, b) => b.length - a.length); // longest first: "Professional Memberships" before "Memberships"
}

/** True when a whole line IS the section label and nothing else — never a bullet, never a title. */
export function isSectionLine(line: string, aliases: readonly string[]): boolean {
  const n = normaliseSectionLabel(line);
  return n.length > 0 && aliases.some((a) => normaliseSectionLabel(a) === n);
}

/** A slug-shaped alias — hyphens/underscores. Such a token is a machine id; it is never applicant prose. */
function isSlugLike(alias: string): boolean {
  return /[-_]/.test(alias.trim());
}

/**
 * Drop a leading section label from `text`. THE RULE, and why it cannot eat a real title:
 *
 *   • ANCHORED AT INDEX 0 only, and only against an alias of THIS entry's own section — never a global word list.
 *   • The alias must be followed by a HARD separator: end-of-string, a newline (optionally with a colon or an
 *     en/em dash before it), or a colon / en dash / em dash. These are heading punctuation, not prose.
 *   • A plain SPACE is accepted only when the alias is slug-shaped (`professional-memberships`) AND the remainder
 *     resumes with an upper-case letter or a digit — i.e. a new title begins. That is the flattened form of the
 *     live defect. "clinical-experience in rural Alberta" resumes lower-case and is left alone.
 *   • Case-insensitive, but nothing else about the text is touched — the remainder is returned VERBATIM.
 *
 * So "Education Committee Chair" and "Teaching Award" survive: the alias there is a single word followed by a
 * plain space, which no branch accepts.
 */
export function stripSectionPrefix(text: string, aliases: readonly string[]): string {
  for (const alias of aliases) {
    const a = alias.trim();
    if (a.length === 0 || text.length < a.length) continue;
    if (text.slice(0, a.length).toLowerCase() !== a.toLowerCase()) continue;
    const rest = text.slice(a.length);
    if (rest.length === 0) return '';
    // end-of-heading: optional colon/dash, then a newline (or nothing left but blanks)
    const hard = rest.match(/^[ \t]*[:‒–—]?[ \t]*\n/);
    if (hard) return rest.slice(hard[0].length);
    const punct = rest.match(/^[ \t]*[:‒–—][ \t]*/);
    if (punct) return rest.slice(punct[0].length);
    if (/^[ \t]*$/.test(rest)) return '';
    const spaced = rest.match(/^[ \t]+/);
    if (spaced && isSlugLike(a) && /^[\p{Lu}0-9(]/u.test(rest.slice(spaced[0].length))) return rest.slice(spaced[0].length);
  }
  return text;
}

/**
 * The entry's displayed text: the ledger's `raw_text` VERBATIM, minus the section label — a leading section label
 * (`stripSectionPrefix`) and any whole line that IS the section label (`isSectionLine`, so it can never render as
 * a numbered bullet). Everything else is untouched, character for character (ApplicantText). When the strip is a
 * no-op the ORIGINAL string is returned, so a text with no label in it is provably verbatim.
 */
export function displayedEntryText(rec: LedgerEntryLike | undefined): string | null {
  const raw = stated(rec?.raw_text);
  if (raw === null) return null;
  const aliases = sectionAliasesOf(rec);
  if (aliases.length === 0) return raw;
  const stripped = stripSectionPrefix(raw, aliases);
  const lines = stripped.split('\n');
  const kept = lines.filter((l) => !isSectionLine(l, aliases));
  const out = kept.length === lines.length ? stripped : kept.join('\n');
  if (out === raw) return raw;
  return out.trim().length > 0 ? out : null;
}

/**
 * The entry's own text as the legacy left-column points — ONE PER LINE, the applicant's words verbatim.
 *
 * This is what makes the legacy per-line behaviour real (owner, 2026-08-23: "the feedback is meant to be point 1,
 * point 2, ..."; "a point by pont analysis"). It used to return a SINGLE point holding the entire, possibly
 * six-bullet, `raw_text`, so the numbered list had one entry in it and Previous/Next had one row to walk. Splitting
 * on the entry's own line breaks restores the numbered rows the owner ratified; blank lines are dropped so a
 * stray newline never becomes an empty numbered row. None without a ledger.
 */
function textPointOf(id: string, ledger: LedgerView): QSiblingBullet[] | undefined {
  const text = displayedEntryText(ledger.byId.get(id));
  if (text === null) return undefined;
  const lines = text.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length === 0) return undefined;
  return lines.map((bullet_text, i) => ({
    bullet_id: entryTextPointId(id, i),
    bullet_text,
    position: i + 1,
    flagged: false,
  }));
}

/**
 * The entry's LEFT-COLUMN text for its rows — the applicant's own words for the entry (the thing the finding is
 * about). Fallback chain (Sol one-pass on the restored flow, 2026-08-23): the entry title (role title → institution
 * → raw text, `identityOf`) → the first line of the entry's displayed text (`displayedEntryText`, so never the
 * section slug) → the registered generic "Not stated" value label (`CS-VAL-NOT-STATED`: the ledger states no title
 * and no text for the entry — the same label the facts screen gives any unstated value; no entry-specific
 * unreadable label is registered) → null only if that registered label fails the scan. A null means NO left-hand
 * text, and `rowsForEntry` makes no row for it — a blank left-hand row beside a prompt is never rendered.
 */
export function leftTextOf(entry_id: string, ledger: LedgerView): string | null {
  const title = identityOf(entry_id, ledger).entry_title;
  if (title.length > 0) return title;
  const text = displayedEntryText(ledger.byId.get(entry_id));
  const first = text === null ? undefined : text.split('\n').map((l) => l.trim()).find((l) => l.length > 0);
  if (first !== undefined) return first;
  const notStated = copyText('CS-VAL-NOT-STATED');
  return notStated.length > 0 ? notStated : null;
}

/** The question recommendations of one entry, in payload order. */
export function questionsForEntry(recs: readonly Rec[], entry_id: string): Rec[] {
  return recs.filter((r) => r.entry_id === entry_id && r.kind === 'question_to_answer');
}

/**
 * The entry's rows in the legacy item: one per flag (engine order), then one per question (payload order).
 *
 * The row's left-column text is the entry's own label — the thing the finding is about. The engine's flags and
 * questions are ENTRY-level (omission prompts), not point-level, so there is no bullet of the applicant's to set
 * beside each one; the label is the applicant's own words for the entry (`leftTextOf`), which is what the legacy
 * view already rendered beside an entry-target check. With NO left-hand text (`null`) the entry has no rows: a
 * blank left-hand row is never rendered.
 */
export function rowsForEntry(
  entry: Entry,
  recs: readonly Rec[],
  leftText: string | null,
  lines?: readonly QSiblingBullet[],
): QWeakBullet[] {
  // THE ADAPTER SEAM, AND WHY THE SURFACE STILL DID NOT BEHAVE LIKE THE OLD ONE (owner, 2026-08-23).
  //
  // The legacy read was PER LINE: every CV line of an entry was its own row, the flagged ones coloured, and
  // Previous/Next walked the highlight line to line. The new engine's flags and questions are PER ENTRY, and this
  // adapter used to mint a SYNTHETIC row for each of them whose text was the entry LABEL and whose id
  // (`E2::flag::0`) matched no line the applicant had written. So the rows on screen were entry-level objects
  // sitting beside the entry's real lines instead of being them — which is exactly the behaviour that went
  // missing, and no amount of restyling the block could restore it.
  //
  // NOW THE PROMPT LANDS ON A REAL LINE. The entry's own lines (`all_bullets`, from the ledger's layout facts)
  // are the rows; this function returns the one that carries the finding, keyed by that LINE's id and text, so
  // `displayBulletsOf` merges it into the line list and every other line renders as a clean row. The row walk,
  // the colour code and the numbering then all work on lines, as they did before, with no change to the view.
  //
  // WHAT THIS STILL DOES NOT DO, and it is the owner's own arrival (re-mined from the sessions that produced the
  // legacy surface, 2026-08-23). He settled on a PER-POINT critique — every numbered line carrying its OWN
  // diagnosis and its OWN question: "the feedback is meant to be point 1, point 2, ..."; "a point by pont
  // analysis in the scrollable review panel"; and the shape he pasted back as correct was "Point 3 names
  // participation in ward rounds without describing your own contribution…" followed by that point's own
  // question. The legacy cc-11 classifier emitted per-bullet findings, so it could do that. THE R5 RUBRIC ENGINE
  // EMITS ENTRY-LEVEL FLAGS AND QUESTIONS ONLY — there is nothing per-line for this function to map. Anchoring
  // the entry's prompt to line 1 is the most honest placement available; inventing a critique for each of the
  // other lines would attach a real-sounding finding to a line it was never computed against, which is the one
  // thing this adapter may never do. The gap is in the ENGINE, and it is the remaining reason the surface does
  // not fully behave like the old one. See scratchpad ASSESS-2026-08-23/owner-ui-directives.md §L.1.
  //
  // WHICH LINE. Every prompt the engine currently emits is ENTRY-SCOPED — a missing licence status, an unstated
  // clinical context, an absent date — so the honest anchor is the entry's FIRST line, the one that names the
  // entry. This is deliberately not a guess at "the most relevant line": the engine publishes no per-line signal,
  // and inventing one here would put a real critique next to a line it was never computed against. When the
  // engine does emit per-line findings, the mapping is `line.bullet_id`, not this fallback.
  //
  // ONE BLOCK PER ISSUE. The flag sentences and the question sentences are the row's two text slots — a flagged
  // clinical entry used to render the flag as one block and then a near-identical "Question to answer" block
  // restating the same gap. C2b is unchanged as a GATE (`scopeValid` still decides whether a prompt is made); the
  // inline "Assessed within this entry." tag is gone from the row and survives in the entry's facts disclosure.
  const flags = entry.flags.filter((f) => scopeValid(f.search_scope));
  const questions = questionsForEntry(recs, entry.entry_id)
    .filter((r) => r.evidence.kind === 'closed_world' && scopeValid(r.evidence.search_scope));
  if (flags.length === 0 && questions.length === 0) return [];
  // NOT ANCHORED TO A LINE BY POSITION (Sol review, 2026-08-23). Anchoring the entry's prompt to line 1 implied
  // line-specific analysis that was never performed. Safe distribution across lines needs one of: a provably
  // atomic entry, an engine-supplied `target_line_id`, or evidence provenance resolving to a unique source line —
  // and none exists today. So the finding stays an ENTRY-SCOPED block beside the entry's lines, and the row is
  // keyed by its own system id rather than borrowing a line's.
  if (leftText === null || leftText.length === 0) return [];
  const note = flags.length > 0
    ? joinRegistered(...flags.map((f) => copyText(f.copy_string_id)))
    : copyText(questions[0]!.kind_copy_string_id);
  return [{
    bullet_id: flags.length > 0 ? flagRowId(entry.entry_id, 0) : questionRowId(entry.entry_id, 0),
    bullet_text: leftText,
    gate_id: flags.length > 0 ? ROW_GATE_FLAG : ROW_GATE_QUESTION,
    diagnosis: note,
    question: joinRegistered(...questions.map((r) => (r.copy_string_id ? copyText(r.copy_string_id) : ''))),
  }];
}

/**
 * Two registered strings in ONE legacy text slot, space-joined; an empty (scan-failed) half drops out. The legacy
 * slot renders one string; the registry test (`adaptedSystemStrings`) splits the join back into its registered
 * members, so nothing unregistered can hide inside a join.
 */
export function joinRegistered(...parts: string[]): string {
  return parts.filter((p) => p.length > 0).join(' ');
}

function itemOf(entry: Entry, recs: readonly Rec[], ledger: LedgerView): QItem {
  const identity = identityOf(entry.entry_id, ledger);
  return {
    entry_id: entry.entry_id,
    ...identity,
    gate_id: ITEM_GATE,
    bullets: rowsForEntry(entry, recs, leftTextOf(entry.entry_id, ledger)),
    all_bullets: textPointOf(entry.entry_id, ledger),
  };
}

function strongEntryOf(entry_id: string, ledger: LedgerView): QStrongEntry {
  const identity = identityOf(entry_id, ledger);
  const point = textPointOf(entry_id, ledger);
  return {
    entry_id,
    ...identity,
    bullets: (point ?? []).map((p) => ({ bullet_id: p.bullet_id, bullet_text: p.bullet_text })),
  };
}

/** A ledger-only entry (the engine did not assess it): an item with no rows, present for the correction loop. */
function ledgerOnlyItemOf(entry_id: string, ledger: LedgerView): QItem {
  return {
    entry_id,
    ...identityOf(entry_id, ledger),
    gate_id: ITEM_GATE,
    bullets: [],
    all_bullets: textPointOf(entry_id, ledger),
  };
}

/* ============================================================================================================== *
 * THE SECTIONS
 * ============================================================================================================== */

/** The document-supplied section label the ledger grounded for this entry (`${id}:section_heading`), if any. */
function sectionHeadingOf(rec: LedgerEntryLike | undefined): string | null {
  const facts = Array.isArray(rec?.facts) ? (rec!.facts as Array<{ field?: unknown; value?: unknown; user_value?: unknown }>) : null;
  const f = terminalFact(facts as Array<{ field: string; value?: unknown; user_value?: unknown }> | null, 'section_heading');
  if (f === null) return null;
  return stated(f.user_value) ?? stated(f.value);
}

/** `source_section` normalised to the §2.2 enum's own spelling (lower-case, `_` for `-`/space, trimmed). */
function normaliseSourceSection(value: unknown): string | null {
  const v = stated(value);
  return v === null ? null : v.trim().toLowerCase().replace(/[-\s]+/g, '_');
}

/**
 * The comparison form for a section label: lower-case, `-`/`_`/whitespace collapsed to a single space, trailing
 * punctuation (`:`, en/em dash) dropped. It is the ONE normalisation the slug-leak fix compares through, so
 * `professional-memberships`, `Professional Memberships` and `PROFESSIONAL MEMBERSHIPS:` are one thing.
 */
export function normaliseSectionLabel(s: string): string {
  return s.trim().toLowerCase().replace(/[-_\s]+/g, ' ').replace(/[\s:\u2012\u2013\u2014]+$/, '').trim();
}

/**
 * The CV's own section display name for a taxonomy id (`clinical-experience` → "Clinical Experience"), else null.
 * SLUG-LEAK FIX (2026-08-23): a DISPLAY NAME resolves too ("Professional Memberships" → "Professional
 * Memberships"), and matching runs through `normaliseSectionLabel`, so the assembled first line can carry the
 * human heading (the worker now renders that, not the slug) without the taxonomy chain degrading to "Other".
 */
export function taxonomySectionLabel(slug: string | null): string | null {
  if (slug === null) return null;
  const id = normaliseSectionLabel(slug);
  if (id.length === 0) return null;
  const hit = OUTPUT_SECTIONS.find(
    (s) => normaliseSectionLabel(s.label) === id || s.members.some((m) => normaliseSectionLabel(m) === id),
  );
  return hit ? hit.label : null;
}

/** The CV-section display names the adapter can supply from the taxonomy (NOT registered copy: see `sectionLabelOf`). */
export const TAXONOMY_SECTION_LABELS: ReadonlySet<string> = new Set(OUTPUT_SECTIONS.map((s) => s.label));

/**
 * The CV-section label for an entry.
 *
 *   1. The ledger's `source_section`, normalised, through its REGISTERED enum label — for every enum member except
 *      `unlabelled`, which is the extractor's degraded value for any document section id OUTSIDE the closed §2.2
 *      enum (e.g. `clinical-experience`; see `toSourceSection`) and must never head a known section as "No section
 *      heading".
 *   2. Otherwise the document's OWN section label (the grounded `section_heading` fact, else a non-enum
 *      `source_section` value), through the CV's section taxonomy — the display name the legacy read grouped
 *      under. This is the document's structural label, not system copy (it is the CV's own section, exactly as the
 *      legacy view showed it), so `adaptedSystemStrings` excludes it.
 *   3. Only when no section label exists at all: the registered neutral "Other".
 *   Without a ledger: the registered fallback section, as before.
 */
export function sectionLabelOf(entry_id: string, ledger: LedgerView): string {
  const rec = ledger.byId.get(entry_id);
  if (rec === undefined) return copyText('CS-SECTION-FACETS');
  // 0. SLUG-LEAK FIX: the document's OWN printed heading, when one was grounded, outranks the coarse enum word —
  //    it is the section the applicant actually wrote ("Professional Memberships"), and since `toSourceSection`
  //    now maps a labelled-but-off-enum section to `other`, the enum rung would otherwise flatten it to "Other".
  const grounded = taxonomySectionLabel(sectionHeadingOf(rec));
  if (grounded !== null) return grounded;
  const normalised = normaliseSourceSection(rec.source_section);
  const id = enumLabelFor('source_section', normalised);
  if (id !== null && id !== 'CS-VAL-SOURCE-SECTION-UNLABELLED') return copyText(id);
  const taxonomy = taxonomySectionLabel(sectionHeadingOf(rec)) ?? taxonomySectionLabel(stated(rec.source_section));
  if (taxonomy !== null) return taxonomy;
  return copyText('CS-VAL-SOURCE-SECTION-OTHER');
}

/**
 * One registered "{name}: {band}." sentence (`CS-OVERVIEW-BAND-SENTENCE`) for the legacy opening line. Both slots are
 * registered text (never applicant text here), so the fill is scanned as system copy end to end; an empty slot
 * (a scan-failed half) yields no sentence at all rather than a dangling "Name: .".
 */
export function bandSentence(name: string, band: string): string {
  if (name.length === 0 || band.length === 0) return '';
  return fillTemplate(COPY['CS-OVERVIEW-BAND-SENTENCE'], { name, band }, 'CS-OVERVIEW-BAND-SENTENCE');
}

/**
 * The band slot of a domain's own sentence. The band label for every band — EXCEPT a "Not shown" domain whose closed-world
 * scope is present and valid (C2b), which carries the §4.7 claim IN FULL in the one neutral-ink headline slot the
 * legacy card has: the mandatory sentence, the extended sentence, then the scope disclosure with the scope's own
 * count (`CS-OMISSION-SCOPE`, the sole allowlisted count, T-COUNT-1). A "Not shown" domain with no valid scope
 * falls back to the band label alone — the claim is not made without the closed world it was assessed over.
 */
export function domainHeadline(d: RubricReadPublicV1['domains'][number]): string {
  const ns = d.not_shown;
  if (ns && scopeValid(ns.search_scope)) {
    return joinRegistered(
      copyText(ns.copy_string_id),
      copyText(ns.extended_copy_string_id),
      fillTemplate(COPY['CS-OMISSION-SCOPE'], { n: String(ns.search_scope.n_assessed) }, 'CS-OMISSION-SCOPE'),
    );
  }
  return copyText(d.band_copy_string_id);
}

/**
 * The domain's §4.6 line: `CS-WHAT-WOULD-CHANGE` with the ENGINE's own target entry (the `{x}` slot is applicant
 * text and exempt from the scan exactly as `DomainBands` renders it), else `CS-SATURATION` when the engine says
 * so, else nothing.
 */
export function domainNextLine(d: RubricReadPublicV1['domains'][number], ledger: LedgerView): string {
  const target = d.documentation_target_entry_id;
  const label = target === null ? undefined : ledger.headings.get(target)?.text;
  if (target !== null && label !== undefined) {
    return fillTemplate(
      COPY['CS-WHAT-WOULD-CHANGE'], { x: { source: 'applicant_verbatim', text: label } }, 'CS-WHAT-WOULD-CHANGE',
    );
  }
  if (d.saturation_copy_string_id) return copyText(d.saturation_copy_string_id);
  return '';
}

/** Registered domain labels as one list slot: "A", "A and B", "A, B and C" (", " then " and "; A.5-P.17). */
export function joinDomainLabels(labels: readonly string[]): string {
  const xs = labels.filter((l) => l.length > 0);
  if (xs.length <= 1) return xs.join('');
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

/**
 * A.5-P.17 — ONE registered sentence for every "Not shown" domain sharing the same valid closed-world scope count
 * (`CS-OVERVIEW-NOT-SHOWN-GROUP`: the §4.7 claim + extended sentence + the scope disclosure, grouped). `{n}` is the
 * scope's own count (the same provenance disclosure `CS-OMISSION-SCOPE` carries — C2b, T-COUNT-1); `{domains}` the
 * members' registered `CS-DOMAIN-*` labels, joined. Both slots are scanned as system text.
 */
export function notShownGroupSentence(n: number, domainLabels: readonly string[]): string {
  const domains = joinDomainLabels(domainLabels);
  if (domains.length === 0) return '';
  return fillTemplate(COPY['CS-OVERVIEW-NOT-SHOWN-GROUP'], { n: String(n), domains }, 'CS-OVERVIEW-NOT-SHOWN-GROUP');
}

/** A "Not shown" domain whose closed-world scope is present and valid (C2b) — the only kind that is grouped. */
function validNotShownScope(d: RubricReadPublicV1['domains'][number]): { n_assessed: number } | null {
  const ns = d.not_shown;
  return ns && scopeValid(ns.search_scope) ? ns.search_scope : null;
}

/**
 * THE DOMAIN BANDS, as the opening line's sentences, in the engine's (fixed `DOMAIN_ORDER`) order:
 *   * a DOCUMENTED domain (and a Not-shown domain with no valid scope, C2b fail-closed: band label alone) keeps its
 *     own `CS-OVERVIEW-BAND-SENTENCE` ("{name}: {band}.");
 *   * the NOT-SHOWN domains with a valid scope are collapsed into ONE `CS-OVERVIEW-NOT-SHOWN-GROUP` sentence per
 *     distinct scope count (Sol one-pass, 2026-08-23 — a thin CV read five near-identical claims), placed at the
 *     position of the group's first member; the scope count is carried (C2b);
 *   * each domain's §4.6 line (`CS-WHAT-WOULD-CHANGE` / `CS-SATURATION`) follows at the domain's own position.
 * Space-joined registered text — the registry test splits the join back into its members.
 */
export function domainBandsLine(read: RubricReadPublicV1, ledger: LedgerView): string {
  const groups = new Map<number, string[]>();
  for (const d of read.domains) {
    const scope = validNotShownScope(d);
    if (scope === null) continue;
    const labels = groups.get(scope.n_assessed) ?? [];
    labels.push(copyText(d.domain_copy_string_id));
    groups.set(scope.n_assessed, labels);
  }
  const emitted = new Set<number>();
  return joinRegistered(...read.domains.flatMap((d) => {
    const scope = validNotShownScope(d);
    const parts: string[] = [];
    if (scope === null) {
      parts.push(bandSentence(copyText(d.domain_copy_string_id), domainHeadline(d)));
    } else if (!emitted.has(scope.n_assessed)) {
      emitted.add(scope.n_assessed);
      parts.push(notShownGroupSentence(scope.n_assessed, groups.get(scope.n_assessed) ?? []));
    }
    parts.push(domainNextLine(d, ledger));
    return parts;
  }));
}

/**
 * THE PRESENTATION BANDS, as the opening line's sentences: the aggregate (custom CV only, §4.10) then one per
 * visible sub-element, in `SUB_ELEMENT_ORDER`. Empty when nothing is shown (the CaRMS surface with every
 * sub-element suppressed).
 */
export function presentationLine(read: RubricReadPublicV1): string {
  const p = read.presentation;
  const parts: string[] = [];
  if (showsAggregateBand(p, read.input_surface) && p.overall_copy_string_id) {
    parts.push(bandSentence(copyText('CS-SECTION-PRESENTATION'), copyText(p.overall_copy_string_id)));
  }
  for (const s of visibleSubElements(p.sub_elements, read.input_surface)) {
    parts.push(bandSentence(copyText(s.copy_string_id), copyText(s.band_copy_string_id)));
  }
  return joinRegistered(...parts);
}

/**
 * The section card's "Do this first" slot: the section's FIRST prompt — the first row of its first entry with
 * rows, which is a flag (its `CS-FLAG-*` sentence + the per-entry scope disclosure) or a question (`CS-Q-*` + the
 * disclosure; the row's own text) — else the registered `CS-NOTHING-FLAGGED`. The same registered text the row
 * carries at L2, so the card previews the section's first point to check without adding a claim of its own.
 */
/**
 * THE DATE QUESTIONS, BY REGISTRY — every `CS-Q-*` id whose subject is when the entry happened. Derived from the
 * registry's own id space (`/^CS-Q-DATE/`), never from a hard-coded pair and never by matching the sentence text,
 * so a future date variant (`CS-Q-DATE-END`, …) is caught the day it is registered and a copy edit cannot break
 * the rule. The strings, not the ids, are what a built row carries, so this resolves to registered TEXT.
 */
const DATE_QUESTION_TEXTS: readonly string[] = (Object.keys(COPY) as CopyStringId[])
  .filter((id) => /^CS-Q-DATE/.test(id))
  .map((id) => copyText(id))
  .filter((t) => t.length > 0);

/** True when this question row's registered `CS-Q-*` sentence is one of the date questions. */
function isDateQuestionRow(b: QWeakBullet): boolean {
  return b.gate_id === ROW_GATE_QUESTION && DATE_QUESTION_TEXTS.some((t) => b.question.startsWith(t));
}

export function sectionRecommendation(items: readonly QItem[]): string {
  // HEADLINE SELECTION (owner, live: "even where asked it must not be the headline banner"). The slot used to take
  // the first bullet of the FIRST item with bullets, so a bare dates question on entry 1 crowded out a substantive
  // flag on entry 3. It now scans EVERY item's bullets in document order and previews, in preference:
  //   (a) a flag row — always more substantive than a question;
  //   (b) a question that is not a date question;
  //   (c) a date question;
  //   (d) nothing flagged (below).
  // T-ORDER-1 IS INTACT: nothing is ordered by band or by content. The card's item list, the entries within it and
  // the rows within each entry are all untouched and still in document order; only WHICH already-present prompt is
  // previewed in the banner changes, and the tie-break within each category is still document order.
  const all = items.flatMap((it) => it.bullets);
  const first = all.find((b) => b.gate_id === ROW_GATE_FLAG)
    ?? all.find((b) => b.gate_id === ROW_GATE_QUESTION && !isDateQuestionRow(b))
    ?? all[0];
  if (first === undefined) return copyText('CS-NOTHING-FLAGGED');
  // ONE INSTRUCTION, NOT A CONCATENATION (owner directive 2026-08-23: "direct and concise as to what was
  // deficient and what needed to be done"). Both row shapes used to render BOTH of the legacy row's text slots
  // here, and on both shapes the second slot is the per-entry closed-world disclosure `CS-OMISSION-SCOPE-ENTRY`
  // — provenance, not an instruction. Reading it twice (once on the card, once on the row it previews) is exactly
  // the repetition the owner called out, and it pushed the actual instruction to the middle of the slot.
  //   * a FLAG row's instruction is its `CS-FLAG-*` sentence (`diagnosis`);
  //   * a QUESTION row's instruction is its `CS-Q-*` sentence, which is `question` with the same disclosure
  //     joined onto it — so the disclosure is dropped back off, leaving the registered `CS-Q-*` string whole.
  // The disclosure is NOT lost: it renders on the row itself at L2, where the claim it scopes is made. Both
  // branches still return ONE COMPLETE REGISTERED STRING, which is what `adaptedSystemStrings` re-checks.
  if (first.gate_id === ROW_GATE_FLAG) return first.diagnosis;
  const scope = copyText('CS-OMISSION-SCOPE-ENTRY');
  return scope.length > 0 && first.question.endsWith(scope)
    ? first.question.slice(0, -scope.length).trimEnd()
    : first.question;
}

function cvSectionCards(read: RubricReadPublicV1, ledger: LedgerView): SectionQuality[] {
  // Insertion-ordered: the document's own first-appearance order of each section, entries in read order within.
  const groups = new Map<string, { items: QItem[]; strong: QStrongEntry[] }>();
  const groupFor = (name: string) => groups.get(name) ?? groups.set(name, { items: [], strong: [] }).get(name)!;
  const seen = new Set<string>();
  for (const entry of read.entries) {
    seen.add(entry.entry_id);
    const g = groupFor(sectionLabelOf(entry.entry_id, ledger));
    const item = itemOf(entry, read.recommendations, ledger);
    if (item.bullets.length > 0) g.items.push(item);
    else g.strong.push(strongEntryOf(entry.entry_id, ledger));
  }
  for (const id of ledger.order) {
    if (seen.has(id)) continue;
    groupFor(sectionLabelOf(id, ledger)).items.push(ledgerOnlyItemOf(id, ledger));
  }
  return [...groups].map(([section, g]) => ({
    section,
    // NEVER RENDERED: `total: 0` keeps every rating-keyed line off the card (no headline, no verdict line, and —
    // with the details open — no legacy "not enough material … impact-framing rating" suffix).
    rating: 'adequate',
    total: 0,
    items: g.items,
    // The legacy "Do this first" slot: the section's first prompt, else "nothing flagged" (registered).
    recommendation: sectionRecommendation(g.items) || undefined,
    ...(g.strong.length > 0 ? { strongEntries: g.strong } : {}),
  }));
}

/* ============================================================================================================== *
 * THE READ
 * ============================================================================================================== */

/**
 * A.5-P.15 — the CV-level confidence sentence for the legacy opening line. The engine stamps `review_confidence`
 * with the ratified PER-ENTRY `CS-CONF-*` ids ("We read this entry clearly."); the opening line is about the whole
 * CV, so the surface renders the review-level equivalent for the same level. Keyed by LEVEL (the engine's stamped
 * id is the per-entry wording by construction); an unknown level falls back to the engine's own id.
 */
/**
 * A.5-P.21 — THE PRIORITY SENTENCE of the two-sentence opening line: WHERE TO START.
 *
 * A pure function of the section cards this adapter just built, so it can never disagree with what L1 shows: if
 * any section carries a point to check, the line points the reader at the first one from the top (the cards are
 * already in the document's own order — T-ORDER-1 — so "the first section that lists points to check" is a
 * position, not a ranking, and nothing is re-sorted to produce it). Otherwise it states the absence of prompts.
 * It names no section, carries no count and makes no claim about the entries.
 */
export function priorityLine(sections: readonly SectionQuality[]): string {
  const anyPoints = sections.some((s) => s.items.some((it) => it.bullets.length > 0));
  return copyText(anyPoints ? 'CS-OVERVIEW-PRIORITY-START' : 'CS-OVERVIEW-PRIORITY-CLEAR');
}

/**
 * THE BAND SENTENCES, AS THE DISCLOSURE'S BODY — byte-identical to what the opening line used to carry.
 *
 * This is the SAME text, from the SAME two builders, in the SAME order; the only thing that changed is that it is
 * now behind `CS-OVERVIEW-DETAIL-TOGGLE` instead of in front of the reader. Exported as a plain string so the
 * surface tests can assert the band contract without a DOM, and so `OverviewDetail` has exactly one source.
 */
export function overviewDetailText(read: RubricReadPublicV1, ledgerPayload: LedgerPayload | null): string {
  const ledger = viewLedger(ledgerPayload);
  return joinRegistered(domainBandsLine(read, ledger), presentationLine(read));
}

export const REVIEW_CONFIDENCE_COPY: Readonly<Record<RubricReadPublicV1['review_confidence']['level'], CopyStringId>> = Object.freeze({
  high: 'CS-REVIEW-CONF-HIGH',
  moderate: 'CS-REVIEW-CONF-MODERATE',
  low: 'CS-REVIEW-CONF-LOW',
});

export function toLegacyQualityRead(read: RubricReadPublicV1, ledgerPayload: LedgerPayload | null): QualityRead {
  const ledger = viewLedger(ledgerPayload);
  const confidence = copyText(REVIEW_CONFIDENCE_COPY[read.review_confidence.level] ?? read.review_confidence.copy_string_id);
  // L1 = the CV's OWN SECTIONS as cards, in the document's order. No domain cards, no presentation cards.
  const sections = cvSectionCards(read, ledger);
  // THE OPENING LINE LEADS WITH THE TWO-SENTENCE PRIORITY SUMMARY (owner directive 2026-08-23, verbatim: "giving
  // users feedback on dimensions will not help but rather overwhelm them… raw data is not helpful as the
  // synthesis is"; Sol on the old line: "serialized output: repeated boilerplate plus eleven label/value fields in
  // prose"). Sentence 1 is the CV-level confidence — what the main pattern is, how clearly the CV read. Sentence
  // 2 is `priorityLine` — where to start. Both are registered.
  //
  // THE BAND SENTENCES NO LONGER RIDE HERE. They render through `OverviewDetail` — `overviewDetailText`, behind
  // the closed-by-default `CS-OVERVIEW-DETAIL-TOGGLE` — which `RubricReadView` mounts in the L3 detail slot at the
  // foot of the read. Nothing was deleted: `domainBandsLine` and `presentationLine` are still exported and still
  // produce byte-identical text, and every registered sentence they build is still on the page. What changed is
  // that the applicant meets a two-sentence summary instead of eleven serialised label/value fields, and asks for
  // the per-area detail when they want it. The assertions that read band text out of `quality-overall` RELOCATED
  // to `[data-testid="rubric-overview-detail-body"]` in the same change; none of them was weakened.
  const overall = joinRegistered(confidence, priorityLine(sections));
  // No `specialty_relevance`: the overlay renders through the rubric specialty panel (see `RubricReadView`'s
  // `LegacyRead`), which carries the hedge, pinned sources, C2b scopes and evidence the legacy chip panel cannot.
  return { lead: confidence, overall, sections };
}

/**
 * Every SYSTEM-authored string the adapted payload carries — for the scan and the registry tests. Applicant text
 * (titles, organizations, the entries' own text, the overlay's entry titles) is excluded by construction: it is
 * exempt from the scan (§12.6 part 0) and is not the registry's.
 */
export function adaptedSystemStrings(q: QualityRead): string[] {
  const out: string[] = [];
  const push = (s: string | undefined) => { if (s !== undefined && s.length > 0) out.push(s); };
  push(q.lead);
  push(q.overall);
  for (const s of q.sections) {
    // A CV-section card named by the document's own taxonomy label is the CV's structure, not system copy.
    if (!TAXONOMY_SECTION_LABELS.has(s.section)) push(s.section);
    push(s.smallSectionState);
    push(s.recommendation);
    for (const it of s.items) {
      for (const b of it.bullets) { push(b.diagnosis); push(b.question); }
    }
  }
  return out;
}
